/**
 * 操作ログ詳細の changes[].field（key）を i18n キーに変換するマッピング。
 * subject_type × フィールド名 → i18n キー（OperationLog.fields.<SubjectType>.<field>）。
 */
export type OperationLogFieldLabelsMap = Record<string, Record<string, string>>

const PREFIX = 'OperationLog.fields'

/**
 * グループ表示などで subject がずれたとき、field 名だけで i18n キーを引くフォールバック。
 * BE の各 subject の allowed_keys に含まれるキーのみ。
 */
const COMMON_FIELD_KEYS: Record<string, string> = {
  // グループ表示で subject が Reservation のままのときの CheckinGuest / Reservation 系
  name: `${PREFIX}.CheckinGuest.name`,
  name_kana: `${PREFIX}.CheckinGuest.name_kana`,
  telephone: `${PREFIX}.CheckinGuest.telephone`,
  total_member: `${PREFIX}.CheckinGuest.total_member`,
  delivery_agreement_status: `${PREFIX}.CheckinGuest.delivery_agreement_status`,
  birth_date: `${PREFIX}.CheckinGuest.birth_date`,
  occupation: `${PREFIX}.CheckinGuest.occupation`,
  nationality: `${PREFIX}.CheckinGuest.nationality`,
  gender: `${PREFIX}.CheckinGuest.gender`,
  address: `${PREFIX}.CheckinGuest.address`,
  email: `${PREFIX}.CheckinGuest.email`,
  tel: `${PREFIX}.ReservationGuest.tel`,
  pax: `${PREFIX}.GuestRoomAssign.pax`,
  assign_guest_name: `${PREFIX}.GuestRoomAssign.assign_guest_name`,
  checkin_date: `${PREFIX}.Reservation.checkin_date`,
  checkout_date: `${PREFIX}.Reservation.checkout_date`,
  nights: `${PREFIX}.Reservation.nights`,
  reservation_status: `${PREFIX}.Reservation.reservation_status`,
  approved_status: `${PREFIX}.Reservation.approved_status`,
  sales_date: `${PREFIX}.Sale.sales_date`,
  /** 詳細 API: kind=csv_download 等で changes[].field がスラッグのみ。item_label は日本語でも左欄は field 基準 */
  csv_download: 'OperationLog.module.csv_download',
  /** スタッフセッション（subject が Reservation のままのときのフォールバック） */
  session_action: `${PREFIX}.Staff.session_action`,
  /** HotelCuicinUser / SpaceUseHistory / MobileRequestOrder 等で field は常に cuicin_user_id。BE の field_label は subject ごとに異なるため FE で統一 */
  cuicin_user_id: `${PREFIX}._common.cuicin_user_id`,
}

/** スペース利用時間（subject: SpaceUsageDetails / SpaceUsageDetail）。i18n は SpaceUsageDetails.* に統一。 */
const SPACE_USAGE_DETAILS_FIELDS: Record<string, string> = {
  usage_from: `${PREFIX}.SpaceUsageDetails.usage_from`,
  usage_to: `${PREFIX}.SpaceUsageDetails.usage_to`,
  usageFrom: `${PREFIX}.SpaceUsageDetails.usage_from`,
  usageTo: `${PREFIX}.SpaceUsageDetails.usage_to`,
  /** BE が別名で返す場合（設定・実施画面の項目名に合わせる） */
  use_date_start: `${PREFIX}.SpaceUsageDetails.usage_from`,
  use_date_end: `${PREFIX}.SpaceUsageDetails.usage_to`,
  space_id: `${PREFIX}.SpaceUsageDetails.space_id`,
  space_index: `${PREFIX}.SpaceUsageDetails.space_index`,
  pax: `${PREFIX}.SpaceUsageDetails.pax`,
  /** item_label 側で日本語キーが来る場合 */
  人数: `${PREFIX}.SpaceUsageDetails.pax`,
  answer: `${PREFIX}.SpaceUsageDetails.answer`,
  回答: `${PREFIX}.SpaceUsageDetails.answer`,
  reservation_user_name: `${PREFIX}.SpaceUsageDetails.reservation_user_name`,
  user_name: `${PREFIX}.SpaceUsageDetails.reservation_user_name`,
  time_selection_id: `${PREFIX}.SpaceUsageDetails.time_selection_id`,
  space_use_history_id: `${PREFIX}.SpaceUsageDetails.space_use_history_id`,
}

/** スペース利用履歴（subject: SpaceUseHistories / SpaceUseHistory）。i18n は SpaceUseHistories.* に統一。 */
const SPACE_USE_HISTORIES_FIELDS: Record<string, string> = {
  used_at_from: `${PREFIX}.SpaceUseHistories.used_at_from`,
  used_at_to: `${PREFIX}.SpaceUseHistories.used_at_to`,
  usedAtFrom: `${PREFIX}.SpaceUseHistories.used_at_from`,
  usedAtTo: `${PREFIX}.SpaceUseHistories.used_at_to`,
  used_at_from_unixtime: `${PREFIX}.SpaceUseHistories.used_at_from_unixtime`,
  used_at_to_unixtime: `${PREFIX}.SpaceUseHistories.used_at_to_unixtime`,
  space_reservation_id: `${PREFIX}.SpaceUseHistories.space_reservation_id`,
  hotel_id: `${PREFIX}.SpaceUseHistories.hotel_id`,
  detail_id: `${PREFIX}.SpaceUseHistories.detail_id`,
  space_id: `${PREFIX}.SpaceUseHistories.space_id`,
  space_index: `${PREFIX}.SpaceUseHistories.space_index`,
  pax: `${PREFIX}.SpaceUseHistories.pax`,
  answer: `${PREFIX}.SpaceUseHistories.answer`,
  reservation_user_name: `${PREFIX}.SpaceUseHistories.reservation_user_name`,
  is_used_by_guests: `${PREFIX}.SpaceUseHistories.is_used_by_guests`,
  isUsedByGuests: `${PREFIX}.SpaceUseHistories.is_used_by_guests`,
  cancel_free_until_at: `${PREFIX}.SpaceUseHistories.cancel_free_until_at`,
  cancelFreeUntilAt: `${PREFIX}.SpaceUseHistories.cancel_free_until_at`,
  cuicin_user_id: `${PREFIX}._common.cuicin_user_id`,
}

/** 清掃チェックリスト（subject: GuestRoomCleaningCheckLists / GuestRoomCleaningCheckList）。 */
const GUEST_ROOM_CLEANING_CHECK_LIST_FIELDS: Record<string, string> = {
  hotel_id: `${PREFIX}.GuestRoomCleaningCheckLists.hotel_id`,
  cleaning_check_list_id: `${PREFIX}.GuestRoomCleaningCheckLists.cleaning_check_list_id`,
  guest_room_id: `${PREFIX}.GuestRoomCleaningCheckLists.guest_room_id`,
  date_of: `${PREFIX}.GuestRoomCleaningCheckLists.date_of`,
  is_checked: `${PREFIX}.GuestRoomCleaningCheckLists.is_checked`,
}

/** 清掃報告（subject: GuestRoomCleanReport / GuestRoomCleanReports）。 */
const GUEST_ROOM_CLEAN_REPORT_FIELDS: Record<string, string> = {
  guest_room_id: `${PREFIX}.GuestRoomCleanReport.guest_room_id`,
  admin_comment: `${PREFIX}.GuestRoomCleanReport.admin_comment`,
  staff_report: `${PREFIX}.GuestRoomCleanReport.staff_report`,
  date_of: `${PREFIX}.GuestRoomCleanReport.date_of`,
  created_at_unixtime: `${PREFIX}.GuestRoomCleanReport.created_at_unixtime`,
  updated_at_unixtime: `${PREFIX}.GuestRoomCleanReport.updated_at_unixtime`,
}

/** 清掃写真（subject: GuestRoomCleanPhotos / GuestRoomCleanPhoto）。 */
const GUEST_ROOM_CLEAN_PHOTO_FIELDS: Record<string, string> = {
  hotel_id: `${PREFIX}.GuestRoomCleanPhotos.hotel_id`,
  guest_room_id: `${PREFIX}.GuestRoomCleanPhotos.guest_room_id`,
  date_of: `${PREFIX}.GuestRoomCleanPhotos.date_of`,
  photo_url: `${PREFIX}.GuestRoomCleanPhotos.photo_url`,
  /** BE が camelCase で返す場合 */
  photoUrl: `${PREFIX}.GuestRoomCleanPhotos.photo_url`,
}

/** スマレジ取引（subject: SmaregiTransactionHistory / SmaregiTransactionHistories）。 */
const SMAREGI_TRANSACTION_HISTORY_FIELDS: Record<string, string> = {
  sales_id: `${PREFIX}.SmaregiTransactionHistory.sales_id`,
  store_id: `${PREFIX}.SmaregiTransactionHistory.store_id`,
  guest_name: `${PREFIX}.SmaregiTransactionHistory.guest_name`,
  contract_id: `${PREFIX}.SmaregiTransactionHistory.contract_id`,
  terminal_id: `${PREFIX}.SmaregiTransactionHistory.terminal_id`,
  transaction_at: `${PREFIX}.SmaregiTransactionHistory.transaction_at`,
  transaction_side: `${PREFIX}.SmaregiTransactionHistory.transaction_side`,
  pms_transaction_id: `${PREFIX}.SmaregiTransactionHistory.pms_transaction_id`,
  transaction_head_id: `${PREFIX}.SmaregiTransactionHistory.transaction_head_id`,
}

/** スタッフ（subject: Staff）。セッション操作ログ等 */
const STAFF_FIELDS: Record<string, string> = {
  session_action: `${PREFIX}.Staff.session_action`,
  sessionAction: `${PREFIX}.Staff.session_action`,
}

/** subject_type 別のフィールド → i18n キー（BE allowed_keys_by_subject_type と一致） */
export const OPERATION_LOG_FIELD_LABEL_KEYS: OperationLogFieldLabelsMap = {
  Reservation: {
    reservation_id: `${PREFIX}.Reservation.reservation_id`,
    checkin_date: `${PREFIX}.Reservation.checkin_date`,
    checkin_time: `${PREFIX}.Reservation.checkin_time`,
    checkout_time: `${PREFIX}.Reservation.checkout_time`,
    checkout_date: `${PREFIX}.Reservation.checkout_date`,
    nights: `${PREFIX}.Reservation.nights`,
    plan: `${PREFIX}.Reservation.plan`,
    payment: `${PREFIX}.Reservation.payment`,
    accommodation_fee: `${PREFIX}.Reservation.accommodation_fee`,
    point_discount: `${PREFIX}.Reservation.point_discount`,
    total_amount: `${PREFIX}.Reservation.total_amount`,
    meal: `${PREFIX}.Reservation.meal`,
    notes: `${PREFIX}.Reservation.notes`,
    reservation_status: `${PREFIX}.Reservation.reservation_status`,
    approved_status: `${PREFIX}.Reservation.approved_status`,
    approved_status_updated_at: `${PREFIX}.Reservation.approved_status_updated_at`,
    market_segment_id: `${PREFIX}.Reservation.market_segment_id`,
    channel_code_id: `${PREFIX}.Reservation.channel_code_id`,
  },
  CheckIn: {
    delete_time: `${PREFIX}.CheckIn.delete_time`,
    stay_read: `${PREFIX}.CheckIn.stay_read`,
    checkout_read: `${PREFIX}.CheckIn.checkout_read`,
  },
  Sale: {
    sales_date: `${PREFIX}.Sale.sales_date`,
    reservation_id: `${PREFIX}.Sale.reservation_id`,
    payment_at_unixtime: `${PREFIX}.Sale.payment_at_unixtime`,
    primary_sales_subject_name: `${PREFIX}.Sale.primary_sales_subject_name`,
    is_confirmed: `${PREFIX}.Sale.is_confirmed`,
  },
  ReservationGuest: {
    reservation_id: `${PREFIX}.ReservationGuest.reservation_id`,
    name: `${PREFIX}.ReservationGuest.name`,
    name_kana: `${PREFIX}.ReservationGuest.name_kana`,
    tel: `${PREFIX}.ReservationGuest.tel`,
    mail_addr: `${PREFIX}.ReservationGuest.mail_addr`,
    addr: `${PREFIX}.ReservationGuest.addr`,
  },
  ReservationUser: {
    reservation_id: `${PREFIX}.ReservationUser.reservation_id`,
    name: `${PREFIX}.ReservationUser.name`,
    name_kana: `${PREFIX}.ReservationUser.name_kana`,
    tel: `${PREFIX}.ReservationUser.tel`,
    mail_addr: `${PREFIX}.ReservationUser.mail_addr`,
    addr: `${PREFIX}.ReservationUser.addr`,
  },
  ReservationNote: {
    reservation_id: `${PREFIX}.ReservationNote.reservation_id`,
    memo: `${PREFIX}.ReservationNote.memo`,
    memo_front: `${PREFIX}.ReservationNote.memo_front`,
    memo_cleaning: `${PREFIX}.ReservationNote.memo_cleaning`,
    memo_meal: `${PREFIX}.ReservationNote.memo_meal`,
  },
  CheckinGuest: {
    name: `${PREFIX}.CheckinGuest.name`,
    name_kana: `${PREFIX}.CheckinGuest.name_kana`,
    nationality: `${PREFIX}.CheckinGuest.nationality`,
    gender: `${PREFIX}.CheckinGuest.gender`,
    postal_code: `${PREFIX}.CheckinGuest.postal_code`,
    address: `${PREFIX}.CheckinGuest.address`,
    email: `${PREFIX}.CheckinGuest.email`,
    telephone: `${PREFIX}.CheckinGuest.telephone`,
    total_member: `${PREFIX}.CheckinGuest.total_member`,
    occupation: `${PREFIX}.CheckinGuest.occupation`,
    other_occupation: `${PREFIX}.CheckinGuest.other_occupation`,
    company: `${PREFIX}.CheckinGuest.company`,
    birth_date: `${PREFIX}.CheckinGuest.birth_date`,
    delivery_agreement_status: `${PREFIX}.CheckinGuest.delivery_agreement_status`,
  },
  SalesSubject: {
    sales_subject_master_id: `${PREFIX}.SalesSubject.sales_subject_master_id`,
    sales_sub_subject_id: `${PREFIX}.SalesSubject.sales_sub_subject_id`,
    sales_discount_master_id: `${PREFIX}.SalesSubject.sales_discount_master_id`,
    quantity: `${PREFIX}.SalesSubject.quantity`,
    price: `${PREFIX}.SalesSubject.price`,
    guest_room_assign_id: `${PREFIX}.SalesSubject.guest_room_assign_id`,
  },
  UploadReceiptLog: {
    file_name: `${PREFIX}.UploadReceiptLog.file_name`,
    receipt_name: `${PREFIX}.UploadReceiptLog.receipt_name`,
    reservation_id: `${PREFIX}.UploadReceiptLog.reservation_id`,
  },
  Hotel: {
    hotel_name: `${PREFIX}.Hotel.hotel_name`,
    hotel_name_latin: `${PREFIX}.Hotel.hotel_name_latin`,
    telephone: `${PREFIX}.Hotel.telephone`,
    checkin_begin_time: `${PREFIX}.Hotel.checkin_begin_time`,
    checkin_end_time: `${PREFIX}.Hotel.checkin_end_time`,
    checkout_begin_time: `${PREFIX}.Hotel.checkout_begin_time`,
    checkout_end_time: `${PREFIX}.Hotel.checkout_end_time`,
    receipt_hotel_name: `${PREFIX}.Hotel.receipt_hotel_name`,
    receipt_address: `${PREFIX}.Hotel.receipt_address`,
    receipt_telephone: `${PREFIX}.Hotel.receipt_telephone`,
    receipt_registration_number: `${PREFIX}.Hotel.receipt_registration_number`,
    receipt_display_method: `${PREFIX}.Hotel.receipt_display_method`,
  },
  GuestRoom: {
    room_type_id: `${PREFIX}.GuestRoom.room_type_id`,
    room_number: `${PREFIX}.GuestRoom.room_number`,
    room_floor_id: `${PREFIX}.GuestRoom.room_floor_id`,
    key_number: `${PREFIX}.GuestRoom.key_number`,
    assign_order: `${PREFIX}.GuestRoom.assign_order`,
    display_order: `${PREFIX}.GuestRoom.display_order`,
    is_display: `${PREFIX}.GuestRoom.is_display`,
    is_specified: `${PREFIX}.GuestRoom.is_specified`,
    cleaning_status: `${PREFIX}.GuestRoom.cleaning_status`,
    capacity: `${PREFIX}.GuestRoom.capacity`,
    remote_device_id: `${PREFIX}.GuestRoom.remote_device_id`,
  },
  AdvanceMoneyHistory: {
    company_id: `${PREFIX}.AdvanceMoneyHistory.company_id`,
    payment_amount: `${PREFIX}.AdvanceMoneyHistory.payment_amount`,
    payment_date_unixtime: `${PREFIX}.AdvanceMoneyHistory.payment_date_unixtime`,
  },
  SalesPayment: {
    payment_method_id: `${PREFIX}.SalesPayment.payment_method_id`,
    payment_method_master_id: `${PREFIX}.SalesPayment.payment_method_master_id`,
    price: `${PREFIX}.SalesPayment.price`,
    payment_at_unixtime: `${PREFIX}.SalesPayment.payment_at_unixtime`,
    sales_accounts_receivable_master_id: `${PREFIX}.SalesAccountsReceivable.sales_accounts_receivable_master_id`,
  },
  GuestRoomAssign: {
    assign_status: `${PREFIX}.GuestRoomAssign.assign_status`,
    assign_status_updated_at: `${PREFIX}.GuestRoomAssign.assign_status_updated_at`,
    assign_status_stay_at: `${PREFIX}.GuestRoomAssign.assign_status_stay_at`,
    assign_status_checkout_at: `${PREFIX}.GuestRoomAssign.assign_status_checkout_at`,
    assign_guest_name: `${PREFIX}.GuestRoomAssign.assign_guest_name`,
    checkin_date: `${PREFIX}.GuestRoomAssign.checkin_date`,
    checkout_date: `${PREFIX}.GuestRoomAssign.checkout_date`,
    checkin_time: `${PREFIX}.GuestRoomAssign.checkin_time`,
    checkout_time: `${PREFIX}.GuestRoomAssign.checkout_time`,
    is_preparation: `${PREFIX}.GuestRoomAssign.is_preparation`,
    preparation_type: `${PREFIX}.GuestRoomAssign.preparation_type`,
    preparation_memo: `${PREFIX}.GuestRoomAssign.preparation_memo`,
    pax: `${PREFIX}.GuestRoomAssign.pax`,
    pin_number: `${PREFIX}.GuestRoomAssign.pin_number`,
    guest_room_id: `${PREFIX}.GuestRoomAssign.guest_room_id`,
    updated_at: `${PREFIX}.GuestRoomAssign.updated_at`,
  },
  GuestRoomAssignMap: {
    checkin_id: `${PREFIX}.GuestRoomAssignMap.checkin_id`,
  },
  GuestRoomAssignPax: {
    male: `${PREFIX}.GuestRoomAssignPax.male`,
    female: `${PREFIX}.GuestRoomAssignPax.female`,
    child_a: `${PREFIX}.GuestRoomAssignPax.child_a`,
    child_b: `${PREFIX}.GuestRoomAssignPax.child_b`,
    child_c: `${PREFIX}.GuestRoomAssignPax.child_c`,
    child_d: `${PREFIX}.GuestRoomAssignPax.child_d`,
    child_e: `${PREFIX}.GuestRoomAssignPax.child_e`,
    child_f: `${PREFIX}.GuestRoomAssignPax.child_f`,
    child_other: `${PREFIX}.GuestRoomAssignPax.child_other`,
  },
  SpaceReservedInfo: {
    title: `${PREFIX}.SpaceReservedInfo.title`,
    description: `${PREFIX}.SpaceReservedInfo.description`,
    question: `${PREFIX}.SpaceReservedInfo.question`,
    stock_unit: `${PREFIX}.SpaceReservedInfo.stock_unit`,
  },
  /** ゲストアプリのスペース詳細（アセット）。変更ログの field 名は API と突合すること。 */
  Space: {
    title: `${PREFIX}.Space.title`,
    description: `${PREFIX}.Space.description`,
    question: `${PREFIX}.Space.question`,
    images: `${PREFIX}.Space.images`,
    sales_hour_start: `${PREFIX}.Space.sales_hour_start`,
    sales_hour_end: `${PREFIX}.Space.sales_hour_end`,
    sales_start_day: `${PREFIX}.Space.sales_start_day`,
    sales_end_day: `${PREFIX}.Space.sales_end_day`,
    sales_end_hour: `${PREFIX}.Space.sales_end_hour`,
    stock: `${PREFIX}.Space.stock`,
    stock_unit: `${PREFIX}.Space.stock_unit`,
    usage_interval_time: `${PREFIX}.Space.usage_interval_time`,
    is_stock_by_start_time: `${PREFIX}.Space.is_stock_by_start_time`,
    free_cancellation_days: `${PREFIX}.Space.free_cancellation_days`,
    space_price_type: `${PREFIX}.Space.space_price_type`,
    is_free_for_guests: `${PREFIX}.Space.is_free_for_guests`,
    sales_subject_master_id: `${PREFIX}.Space.sales_subject_master_id`,
    charge_price_usage_time_start: `${PREFIX}.Space.charge_price_usage_time_start`,
    charge_price_usage_time_end: `${PREFIX}.Space.charge_price_usage_time_end`,
    charge_price_price: `${PREFIX}.Space.charge_price_price`,
    charge_price_max_price: `${PREFIX}.Space.charge_price_max_price`,
    usageIntervalTime: `${PREFIX}.Space.usage_interval_time`,
    isStockByStartTime: `${PREFIX}.Space.is_stock_by_start_time`,
    stockUnit: `${PREFIX}.Space.stock_unit`,
    spacePriceType: `${PREFIX}.Space.space_price_type`,
    isFreeForGuests: `${PREFIX}.Space.is_free_for_guests`,
    salesSubjectMasterId: `${PREFIX}.Space.sales_subject_master_id`,
  },
  SpaceUsageDetails: SPACE_USAGE_DETAILS_FIELDS,
  /** BE が単数形の subject を返す場合 */
  SpaceUsageDetail: SPACE_USAGE_DETAILS_FIELDS,
  SpaceUseHistories: SPACE_USE_HISTORIES_FIELDS,
  /** BE が単数形の subject を返す場合 */
  SpaceUseHistory: SPACE_USE_HISTORIES_FIELDS,
  Company: {
    name: `${PREFIX}.Company.name`,
    payment_method_master: `${PREFIX}.Company.payment_method_master`,
    advance_money: `${PREFIX}.Company.advance_money`,
    current_balance: `${PREFIX}.Company.current_balance`,
  },
  CustomCheckin: {
    jp_content: `${PREFIX}.CustomCheckin.jp_content`,
    jp_title: `${PREFIX}.CustomCheckin.jp_title`,
  },
  GuestRoomCleanHistory: {
    guest_room_id: `${PREFIX}.GuestRoomCleanHistory.guest_room_id`,
    hotel_id: `${PREFIX}.GuestRoomCleanHistory.hotel_id`,
    date_of: `${PREFIX}.GuestRoomCleanHistory.date_of`,
    staff_report: `${PREFIX}.GuestRoomCleanHistory.staff_report`,
    updated_at_unixtime: `${PREFIX}.GuestRoomCleanHistory.updated_at_unixtime`,
    room_usage_status: `${PREFIX}.GuestRoomCleanHistory.room_usage_status`,
    room_usage_status_updated_at: `${PREFIX}.GuestRoomCleanHistory.room_usage_status_updated_at`,
    cleaning_status: `${PREFIX}.GuestRoomCleanHistory.cleaning_status`,
    cleaning_updated_at: `${PREFIX}.GuestRoomCleanHistory.cleaning_updated_at`,
    cleaning_staff_id: `${PREFIX}.GuestRoomCleanHistory.cleaning_staff_id`,
    guest_room_clean_report: `${PREFIX}.GuestRoomCleanHistory.guest_room_clean_report`,
  },
  GuestRoomCleaningCheckLists: GUEST_ROOM_CLEANING_CHECK_LIST_FIELDS,
  /** BE が単数形の subject を返す場合 */
  GuestRoomCleaningCheckList: GUEST_ROOM_CLEANING_CHECK_LIST_FIELDS,
  GuestRoomCleanReport: GUEST_ROOM_CLEAN_REPORT_FIELDS,
  /** BE が複数形の subject を返す場合 */
  GuestRoomCleanReports: GUEST_ROOM_CLEAN_REPORT_FIELDS,
  GuestRoomCleanPhotos: GUEST_ROOM_CLEAN_PHOTO_FIELDS,
  /** BE が単数形の subject を返す場合 */
  GuestRoomCleanPhoto: GUEST_ROOM_CLEAN_PHOTO_FIELDS,
  HotelCuicinUser: {
    cuicin_user_id: `${PREFIX}._common.cuicin_user_id`,
  },
  SmaregiTransactionHistory: SMAREGI_TRANSACTION_HISTORY_FIELDS,
  /** BE が複数形の subject を返す場合 */
  SmaregiTransactionHistories: SMAREGI_TRANSACTION_HISTORY_FIELDS,
  Staff: STAFF_FIELDS,
  MobileRequestOrder: {
    id: `${PREFIX}.MobileRequestOrder.id`,
    hotel_id: `${PREFIX}.MobileRequestOrder.hotel_id`,
    checkin_id: `${PREFIX}.MobileRequestOrder.checkin_id`,
    status: `${PREFIX}.MobileRequestOrder.status`,
    room_number: `${PREFIX}.MobileRequestOrder.room_number`,
    order_guest_first_name: `${PREFIX}.MobileRequestOrder.order_guest_first_name`,
    order_guest_last_name: `${PREFIX}.MobileRequestOrder.order_guest_last_name`,
    payment_method: `${PREFIX}.MobileRequestOrder.payment_method`,
    request_read: `${PREFIX}.MobileRequestOrder.request_read`,
    created_at_unixtime: `${PREFIX}.MobileRequestOrder.created_at_unixtime`,
    updated_at_unixtime: `${PREFIX}.MobileRequestOrder.updated_at_unixtime`,
  },
  SalesAccountsReceivable: {
    sales_accounts_receivable_master_id: `${PREFIX}.SalesAccountsReceivable.sales_accounts_receivable_master_id`,
  },
}

/**
 * 操作ログ詳細の変更項目の i18n キーを取得する。
 */
export function getOperationLogFieldLabelKey(subjectType: string, fieldKey: string): string | null {
  const key = (fieldKey ?? '').toString().trim()
  const bySubject = OPERATION_LOG_FIELD_LABEL_KEYS[subjectType]
  if (bySubject && Object.prototype.hasOwnProperty.call(bySubject, key)) return bySubject[key]
  if (Object.prototype.hasOwnProperty.call(COMMON_FIELD_KEYS, key)) return COMMON_FIELD_KEYS[key]
  return null
}

/**
 * API の item_label（日本語）→ subject_type。
 */
/** 予約メモの各行表示順（memo_front → memo_cleaning → memo_meal） */
export const RESERVATION_NOTE_MEMO_FIELD_ORDER = ['memo', 'memo_front', 'memo_cleaning', 'memo_meal'] as const

const ITEM_LABEL_JA_TO_SUBJECT: Record<string, string> = {
  予約メモを作成: 'ReservationNote',
  予約メモを更新: 'ReservationNote',
  予約メモを削除: 'ReservationNote',
  売上登録: 'Sale',
  売上更新: 'Sale',
  科目追加: 'SalesSubject',
  金額変更: 'SalesSubject',
  入金登録: 'SalesPayment',
  入金変更: 'SalesPayment',
  売掛先の売掛を登録: 'SalesAccountsReceivable',
  売掛先の売掛を変更: 'SalesAccountsReceivable',
  売掛先の売掛を削除: 'SalesAccountsReceivable',
  予約作成: 'Reservation',
  予約更新: 'Reservation',
  予約削除: 'Reservation',
  客室作成: 'GuestRoom',
  客室更新: 'GuestRoom',
  客室削除: 'GuestRoom',
  客室を作成: 'GuestRoomAssign',
  客室を更新: 'GuestRoomAssign',
  客室を削除: 'GuestRoomAssign',
  客室人数を作成: 'GuestRoomAssignPax',
  客室人数を更新: 'GuestRoomAssignPax',
  客室人数を削除: 'GuestRoomAssignPax',
  宿泊を更新: 'CheckIn',
  チェックインを登録: 'CheckinGuest',
  チェックインを変更: 'CheckinGuest',
  チェックインを削除: 'CheckinGuest',
  スペースを作成: 'Space',
  スペースを更新: 'Space',
  スペースを削除: 'Space',
  スペース利用時間を作成: 'SpaceUsageDetails',
  スペース利用時間を更新: 'SpaceUsageDetails',
  スペース利用時間を削除: 'SpaceUsageDetails',
  /** BE が「Subject 更新」形式で返す場合（Reservation 更新 と同様） */
  'SpaceUsageDetail 作成': 'SpaceUsageDetail',
  'SpaceUsageDetail 更新': 'SpaceUsageDetail',
  'SpaceUsageDetail 削除': 'SpaceUsageDetail',
  'SpaceUsageDetails 作成': 'SpaceUsageDetails',
  'SpaceUsageDetails 更新': 'SpaceUsageDetails',
  'SpaceUsageDetails 削除': 'SpaceUsageDetails',
  スペース利用履歴を作成: 'SpaceUseHistories',
  スペース利用履歴を更新: 'SpaceUseHistories',
  スペース利用履歴を削除: 'SpaceUseHistories',
  /** 清掃写真（BE が Subject + 作成/更新/削除形式で返す場合） */
  'GuestRoomCleanPhoto 作成': 'GuestRoomCleanPhoto',
  'GuestRoomCleanPhoto 更新': 'GuestRoomCleanPhoto',
  'GuestRoomCleanPhoto 削除': 'GuestRoomCleanPhoto',
  'GuestRoomCleanPhotos 作成': 'GuestRoomCleanPhotos',
  'GuestRoomCleanPhotos 更新': 'GuestRoomCleanPhotos',
  'GuestRoomCleanPhotos 削除': 'GuestRoomCleanPhotos',
  清掃写真を作成: 'GuestRoomCleanPhoto',
  清掃写真を更新: 'GuestRoomCleanPhoto',
  清掃写真を削除: 'GuestRoomCleanPhoto',
  スマレジ取引を作成: 'SmaregiTransactionHistory',
  スマレジ取引を更新: 'SmaregiTransactionHistory',
  スマレジ取引を削除: 'SmaregiTransactionHistory',
  'スマレジ取引 作成': 'SmaregiTransactionHistory',
  'スマレジ取引 更新': 'SmaregiTransactionHistory',
  'スマレジ取引 削除': 'SmaregiTransactionHistory',
  'SmaregiTransactionHistory 作成': 'SmaregiTransactionHistory',
  'SmaregiTransactionHistory 更新': 'SmaregiTransactionHistory',
  'SmaregiTransactionHistory 削除': 'SmaregiTransactionHistory',
  スタッフを作成: 'Staff',
  スタッフを更新: 'Staff',
  スタッフを削除: 'Staff',
  'Staff 作成': 'Staff',
  'Staff 更新': 'Staff',
  'Staff 削除': 'Staff',
  ルームオーダーを作成: 'MobileRequestOrder',
  ルームオーダーを更新: 'MobileRequestOrder',
  ルームオーダーを削除: 'MobileRequestOrder',
}

/** grouped の item_label から subject_type を推定。未知の場合は null。 */
export function subjectTypeFromItemLabel(itemLabel: string): string | null {
  const trimmed = (itemLabel ?? '').toString().trim()
  if (!trimmed) return null

  const dotEvent = /^([A-Za-z][A-Za-z0-9]*)\.(created|updated|deleted)$/.exec(trimmed)
  if (dotEvent) {
    const st = dotEvent[1]
    if (Object.prototype.hasOwnProperty.call(OPERATION_LOG_FIELD_LABEL_KEYS, st)) return st
  }

  if (Object.prototype.hasOwnProperty.call(ITEM_LABEL_JA_TO_SUBJECT, trimmed)) return ITEM_LABEL_JA_TO_SUBJECT[trimmed]

  // 例: 「入湯税の入金を変更」「室料の入金を登録」など、先頭が科目名になる動的ラベル
  if (/^.+の入金を(登録|変更|削除)$/.test(trimmed)) return 'SalesPayment'

  const prefix = trimmed.split(/[\s\u3000]+/)[0]
  if (prefix && Object.prototype.hasOwnProperty.call(OPERATION_LOG_FIELD_LABEL_KEYS, prefix)) return prefix
  return null
}

/** フィールド key を持つ subject_type を 1 つ返す。item_label が日本語などで取れないときのフォールバック。 */
export function getSubjectTypeByFieldKey(fieldKey: string): string | null {
  const key = (fieldKey ?? '').toString().trim()
  if (!key) return null
  for (const [subjectType, bySubject] of Object.entries(OPERATION_LOG_FIELD_LABEL_KEYS)) {
    if (bySubject && Object.prototype.hasOwnProperty.call(bySubject, key)) return subjectType
  }
  return null
}
